<template>
  <transition name="fade">
    <FocusTrap v-model:active="trapFocus">
      <div
        :id="popupId"
        :class="popupClasses"
        role="dialog"
        :aria-modal="isModal ? 'true' : 'false'"
        :aria-labelledby="titleId"
        tabindex="-1"
      >
        <h2 v-if="!!$slots.title" class="popup__title" :id="titleId">
          <slot name="title"></slot>
        </h2>
        <div v-if="!!$slots.content" class="popup__content">
          <slot name="content"></slot>
        </div>
        <div v-if="!!$slots.actions" class="popup__actions">
          <slot name="actions"></slot>
        </div>
        <button
          v-if="showCloseButton"
          @click="hide"
          class="popup__close"
          :title="t('global.close')"
          :aria-controls="popupId"
        >
          <IconTimes />
        </button>
      </div>
    </FocusTrap>
  </transition>
</template>

<script setup lang="ts">
  import { FocusTrap } from 'focus-trap-vue'
  import IconTimes from '@/components/icons/Times.vue'
  import { usePopup, type PopupId } from '@/composables/usePopup.ts'
  import { computed, onMounted } from 'vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { useUtil } from '@/composables/useUtil.ts'
  import { useSpecificPopup } from '@/composables/useSpecificPopup.ts'

  const props = defineProps({
    name: {
      type: String,
      required: true,
    },
    position: {
      type: String,
      default: 'center-center',
    },
    isModal: {
      type: Boolean,
      default: false,
    },
    showCloseButton: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: 'brand',
    },
    animateShowing: {
      type: Boolean,
      default: true,
    },
    // How long in ms to wait before showing the popup. Only applied when
    // animateShowing === true.
    showDelay: {
      type: Number,
      default: 500,
    },
    extraClasses: {
      type: Array,
      default: () => [],
    },
  })

  const { t } = useStrings()
  const { cleanCssString } = useUtil()
  const {
    trapFocus,
  } = usePopup()

  const {
    isVisible,
    show,
    hide,
  } = useSpecificPopup(props.name as PopupId)

  onMounted(() => {
    if (isVisible.value) {
      if (props.animateShowing) {
        setTimeout(() => {
          show()
        }, props.showDelay)
      }
      else {
        show()
      }
    }
  })

  const cleanName = cleanCssString(props.name)
  const popupId = `popup-${cleanName}`
  const titleId = `popup-${cleanName}-title`

  const popupClasses = computed(() => {
    let result = ['popup', ...props.extraClasses]
    if (props.position !== 'center-center') {
      result.push(`popup--${cleanCssString(props.position)}`)
    }
    if (isVisible.value) {
      result.push('popup--visible')
    }
    return result.join(' ')
  })
</script>

<style lang="scss">
.popup {
  position: absolute;
  top: 50%;
  transition: all 0.2s ease-in-out 0s;
  left: 50%;
  max-width: 28rem;
  width: 85%;
  z-index: 4000;
  transform: translate(-50%, -50%);
  box-shadow: 2px 2px 9px rgba(0, 0, 0, 0.2);
  padding: $spacer10 $spacer20;
  border-left: 5px solid $community;
  background: $white;
  opacity: 0;

  @media (min-width: $bp-sm) {
    padding: $spacer20 $spacer30;
  }

  &.fade-leave-active {
    opacity: 0;
  }

  &--visible {
    opacity: 1;
  }

  // Classes that change the popup's position.
  &--center-top {
    top: 10%;
    transform: translate(-50%, 0);
  }

  &--left-top {
    top: 5%;
    left: 5%;
    transform: none;
  }

  &--right-top {
    top: 5%;
    right: 5%;
    transform: translate(-50%, 0);
  }

  &--right-center {
    top: 50%;
    right: 5%;
    transform: translate(-50%, 0);
  }

  &--left-bottom {
    top: auto;
    bottom: 5%;
    left: 5%;
    transform: none;
  }

  &--right-bottom {
    top: auto;
    left: auto;
    bottom: 5%;
    right: 5%;
    transform: none;
  }

  &--center-bottom {
    top: auto;
    bottom: $spacer10;

    @media (min-width: $bp-sm) {
      bottom: $spacer40;
    }
  }

  &--static {
    position: relative;
    top: auto;
    left: auto;
    bottom: auto;
    right: auto;
    transform: none;
  }

  // Classes that change the popups visual appearance.
  &--more-spacing {
    padding: $spacer20 $spacer30;

    @media (min-width: $bp-sm) {
      padding: $spacer40 $spacer50 $spacer40 $spacer40;
    }
  }

  &--less-spacing {

    @media (min-width: $bp-sm) {
      padding: $spacer10 $spacer20;
    }
  }

  &--no-border {
    border-left: none;
    padding: $spacer20;

    @media (min-width: $bp-sm) {
      padding: $spacer30;
    }

    &.popup--more-spacing {
      padding: $spacer30;

      @media (min-width: $bp-sm) {
        padding: $spacer40;
      }
    }
  }

  &--medium {
    max-width: 36rem;
    width: 85%;
  }
  &--large {
    max-width: 55rem;
    width: 85%;
  }

  &--full-width-on-mobile {
    @media (max-width: $bp-lg - 1) {
      border-left: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      max-width: 100%;
      padding: $spacer10;
      top: auto;
      transform: none;
    }
  }

  &__title {
    font-weight: bold;

    @media (min-width: $bp-sm) {
      font-size: $font-size-md;
    }
  }

  &__actions {
    margin-top: $spacer10;
    display: flex;
    justify-content: center;

    button,
    a {
      margin: 0 $spacer10;
    }
  }

  &__close {
    position: absolute;
    top: $spacer025;
    right: $spacer05;
    width: 2.5em;
    height: 2.5em;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: $spacer075;
    cursor: pointer;
    transition: $transition-base;
    border: none;
    background: transparent;
    color: $grey-dark;

    &:hover {
      color: $body-color;
    }

    .icon {
      vertical-align: middle;
    }
  }
}
</style>
